import { useMemo, type ReactElement, type ReactNode } from "react";
import { interpret } from "../host/declarative/interpreter";
import type { ComponentRegistry } from "../host/declarative/registry";
import type { SduiNode } from "../host/declarative/types";
import { ExtensionRuntimeProvider } from "../plugin/ExtensionRuntimeProvider";
import { InMemoryMcpTransport, type MockToolHandler } from "./InMemoryMcpTransport";

/**
 * Props for {@link DeclarativeMockHost}.
 *
 * Plugin authors `npm link` the runtime and render `<DeclarativeMockHost>` in
 * their local dev app to exercise the same declarative pipeline the real host
 * uses, but backed by in-memory fakes instead of the platform.
 */
export interface DeclarativeMockHostProps
{
    /**
     * In-memory resource map. Keys are MCP resource URIs; values are SDUI trees
     * that {@link interpret} will render against the supplied registry.
     */
    resources: Record<string, SduiNode>;

    /**
     * In-memory tool map. Keys are MCP tool names; values are handlers invoked
     * when a child component calls `useMcpTool(name).invoke(args)`.
     *
     * Handlers may be sync or async — the transport awaits the result before
     * forwarding it to the caller.
     */
    tools?: Record<string, MockToolHandler>;

    /**
     * URI of the resource rendered as the host's default tree. If the URI is
     * not present in `resources` the host renders the supplied `children`
     * instead — useful for stubs that exercise only tool invocations.
     */
    defaultResourceUri: string;

    /**
     * The same primitive → component registry the real host uses. Passed
     * verbatim to {@link interpret}; the mock host owns no UI of its own.
     */
    registry: ComponentRegistry;

    /**
     * Optional fallback content rendered when `defaultResourceUri` does not
     * resolve to a registered resource. Children also have access to the wired
     * transport via {@link ExtensionRuntimeProvider}, so they can invoke
     * mocked tools and resources directly through the React hooks.
     */
    children?: ReactNode;
}

/**
 * In-memory host for declarative (Contract A) plugin local-dev.
 *
 * Renders a plugin's SDUI resource against the supplied registry and wires an
 * {@link InMemoryMcpTransport} into context so descendant components that use
 * `useMcpResource` / `useMcpTool` resolve against the same fakes.
 *
 * The host is intentionally minimal: it does not simulate permissions, theme
 * propagation, or capability tokens. Its purpose is to exercise the
 * declarative pipeline end-to-end against deterministic in-memory data so
 * plugin authors can iterate without standing up the real platform.
 */
export function DeclarativeMockHost(props: DeclarativeMockHostProps): ReactElement
{
    const { resources, tools, defaultResourceUri, registry, children } = props;

    // Memoise the transport so React doesn't churn the context identity every
    // render — re-rendering this host with stable inputs must not abort
    // in-flight hook calls.
    const transport = useMemo(
        () => new InMemoryMcpTransport(resources, tools ?? {}),
        [resources, tools],
    );

    const tree = resources[defaultResourceUri];
    const renderedTree: ReactNode = tree ? interpret(tree, registry) : children;

    return (
        <ExtensionRuntimeProvider transport={transport}>
            {renderedTree}
            {tree ? children : null}
        </ExtensionRuntimeProvider>
    );
}
